import type { ComposerDraft } from "@rakazo/contracts";
import { ComposerDraftSchema } from "@rakazo/contracts";

/** Per-thread composer draft persistence (survives tab reloads and discards). */

export const COMPOSER_DRAFT_STORAGE_PREFIX = "rakazo.composerDraft.";

type ComposerDraftSubmission = { text: string; snapshot: ComposerDraft };
const pendingSubmissions = new Map<string, ComposerDraftSubmission>();
let unsavedWork = false;

/**
 * The shell reports work a reload would lose: a send (typed or spoken) until the server answers,
 * or picked attachments, which live only in memory.
 */
export function setComposerUnsavedWork(pending: boolean): void {
  unsavedWork = pending;
}

/** A send waiting for its answer, or attachments that only exist in memory. */
export function composerHoldsUnsavedWork(): boolean {
  return pendingSubmissions.size > 0 || unsavedWork;
}

/** Unsaved composer work, or saved text in any composer that has not been sent. */
export function hasUnsentComposerDraft(storage: Storage | null = null): boolean {
  if (composerHoldsUnsavedWork()) return true;
  try {
    const drafts = storage ?? localStorage;
    for (let index = 0; index < drafts.length; index++) {
      const key = drafts.key(index);
      if (key?.startsWith(COMPOSER_DRAFT_STORAGE_PREFIX) && drafts.getItem(key)) return true;
    }
  } catch {
    // Without storage the draft lives only in memory, so treat it as unsent.
    return true;
  }
  return false;
}

type DraftStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function getLocalStorage(): DraftStorage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function composerDraftStorageKey(threadKey: string): string {
  return `${COMPOSER_DRAFT_STORAGE_PREFIX}${threadKey}`;
}

export function readComposerDraft(
  threadKey: string,
  storage: DraftStorage | null = getLocalStorage(),
): string {
  if (!storage || !threadKey) return "";
  try {
    const raw = storage.getItem(composerDraftStorageKey(threadKey)) ?? "";
    try {
      const parsed = ComposerDraftSchema.safeParse(JSON.parse(raw));
      if (parsed.success) return parsed.data.text;
    } catch {
      /* Legacy drafts are plain text. */
    }
    return raw;
  } catch {
    return "";
  }
}

export function writeComposerDraft(
  threadKey: string,
  draft: string,
  storage: DraftStorage | null = getLocalStorage(),
): void {
  pendingSubmissions.delete(threadKey);
  if (!storage || !threadKey) return;
  try {
    if (draft.length === 0) storage.removeItem(composerDraftStorageKey(threadKey));
    else storage.setItem(composerDraftStorageKey(threadKey), draft);
  } catch {
    // Quota or private-mode failures: the draft still lives in memory.
  }
}

/** One editor's recovery revision. Another window may edit the same conversation. */
export function openComposerDraft(
  threadKey: string,
  legacyThreadKey: string,
  storage: DraftStorage | null = getLocalStorage(),
) {
  const key = composerDraftStorageKey(threadKey);
  let revision: string | null = null;
  let value: ComposerDraft = { text: "", mentions: [], skill: null };
  let durable = Boolean(storage);
  try {
    revision = storage?.getItem(key) ?? null;
    if (revision) {
      try {
        const parsed = ComposerDraftSchema.safeParse(JSON.parse(revision));
        if (parsed.success) value = parsed.data;
      } catch {
        if (threadKey === legacyThreadKey) value.text = revision;
        else durable = false;
      }
    }
  } catch {
    durable = false;
  }
  function update(next: ComposerDraft) {
    value = next;
    pendingSubmissions.delete(threadKey);
    try {
      if (!storage || storage.getItem(key) !== revision) throw new Error("Draft revision changed");
      ComposerDraftSchema.parse(next);
      const encoded =
        next.text || next.mentions.length || next.skill
          ? JSON.stringify({ ...next, recoveryId: crypto.randomUUID() })
          : null;
      if (encoded) storage.setItem(key, encoded);
      else storage.removeItem(key);
      revision = encoded;
      durable = true;
      // Unscoped legacy drafts have no owner identity. Keep them untouched;
      // importing or deleting one here could expose another signed-in user's draft.
    } catch {
      durable = false;
    }
    return durable;
  }
  return {
    get value() {
      return value;
    },
    get durable() {
      return durable;
    },
    update,
    begin() {
      const submission = { text: value.text, snapshot: value };
      pendingSubmissions.set(threadKey, submission);
      return submission;
    },
    settle(submission: ComposerDraftSubmission, accepted: boolean) {
      if (pendingSubmissions.get(threadKey) !== submission) return;
      pendingSubmissions.delete(threadKey);
      if (!accepted || JSON.stringify(value) !== JSON.stringify(submission.snapshot)) return;
      // update checks the original revision before removing the recovery copy.
      if (!update({ text: "", mentions: [], skill: null })) return;
      for (const listener of snapshotListeners.get(threadKey) ?? []) listener(submission.snapshot);
    },
    acknowledge(snapshot: ComposerDraft) {
      if (JSON.stringify(value) !== JSON.stringify(snapshot)) return false;
      // An accepted send from another mounted view already cleared this copy.
      try {
        if (storage?.getItem(key) !== null) return false;
        revision = null;
        value = { text: "", mentions: [], skill: null };
        durable = true;
        return true;
      } catch {
        return false;
      }
    },
  };
}
const snapshotListeners = new Map<string, Set<(draft: ComposerDraft) => void>>();
export function subscribeComposerSnapshotAccepted(
  threadKey: string,
  listener: (draft: ComposerDraft) => void,
) {
  const listeners = snapshotListeners.get(threadKey) ?? new Set();
  snapshotListeners.set(threadKey, listeners);
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (!listeners.size) snapshotListeners.delete(threadKey);
  };
}
